import { useEffect, useRef, useState } from "react";

// The headline band for Jobs Logged / Accepted — the Phase-1 showcase report.
// Same visual family as the Dashboard (HomeBand.jsx): Stat blocks, hand-rolled
// SVG columns in the theme primary, hairline grid, shared overlay tooltips.
// KPI row: YTD logged/accepted with vs-last-year deltas (completed months
// only, so the comparison is like-for-like), acceptance rate, weeks on
// target. Chart: the 52 weeks as columns against the 50/week target line,
// with Accepted overlaid as a line (bars vs line = readable without colour).

const shortDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });

function Stat({ label, value, delta = null, deltaLabel = "" }) {
  return (
    <div className="text-nowrap">
      <div className="text-muted text-uppercase" style={{ fontSize: "0.62rem", letterSpacing: "0.06em" }}>
        {label}
      </div>
      <div className="fs-4 fw-semibold lh-1">
        {value}
        {delta !== null && delta !== 0 ? (
          <span
            className={`ms-2 fs-6 fw-semibold ${delta > 0 ? "text-success-emphasis" : "text-danger-emphasis"}`}
            data-hint={deltaLabel}
          >
            {delta > 0 ? "▲" : "▼"} {Math.abs(delta)}
          </span>
        ) : null}
      </div>
      {deltaLabel ? (
        <div className="text-muted" style={{ fontSize: "0.62rem" }}>{deltaLabel}</div>
      ) : null}
    </div>
  );
}

function WeeklyChart({ weeks, target }) {
  const wrapRef = useRef(null);
  const [W, setW] = useState(980);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(700, el.clientWidth)));
    ro.observe(el);
    setW(Math.max(700, el.clientWidth));
    return () => ro.disconnect();
  }, []);

  const H = 150;
  const PAD = { top: 8, right: 52, bottom: 16, left: 28 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const max = Math.max(target, ...weeks.map((w) => w.logged));
  const slot = plotW / weeks.length;
  const barW = Math.min(14, Math.max(3, slot - 2)); // 2px surface gap
  const y = (v) => PAD.top + plotH - (v / max) * plotH;

  const step = max <= 20 ? 5 : max <= 40 ? 10 : max <= 100 ? 20 : 50;
  const ticks = [];
  for (let t = 0; t <= max; t += step) ticks.push(t);

  const done = weeks.filter((w) => !w.future);
  const acceptedPoints = done
    .map((w, i) => `${PAD.left + i * slot + slot / 2},${y(w.accepted)}`)
    .join(" ");
  const lastDone = done[done.length - 1];
  const lastDoneX = PAD.left + (done.length - 1) * slot + slot / 2;

  // One month label at the first week of each month.
  const monthTicks = [];
  let seen = "";
  weeks.forEach((w, i) => {
    if (w.month !== seen) {
      seen = w.month;
      monthTicks.push({ x: PAD.left + i * slot + slot / 2, label: w.month.slice(0, 3) });
    }
  });

  return (
    <div ref={wrapRef}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        role="img"
        aria-label="Jobs logged per week against the weekly target, with accepted jobs"
        className="home-band-chart"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className="grid" />
            <text x={PAD.left - 5} y={y(t) + 3} textAnchor="end" className="tick">{t}</text>
          </g>
        ))}
        {monthTicks.map((m) => (
          <text key={m.x} x={m.x} y={H - 4} textAnchor="middle" className="tick">{m.label}</text>
        ))}
        {weeks.map((w, i) => {
          const cx = PAD.left + i * slot + slot / 2;
          const h = Math.max(0, (w.logged / max) * plotH);
          const top = y(w.logged);
          const r = w.logged > 0 ? Math.min(4, h / 2, barW / 2) : 0;
          const path =
            !w.future && w.logged > 0
              ? `M ${cx - barW / 2} ${PAD.top + plotH}
                 V ${top + r}
                 Q ${cx - barW / 2} ${top} ${cx - barW / 2 + r} ${top}
                 H ${cx + barW / 2 - r}
                 Q ${cx + barW / 2} ${top} ${cx + barW / 2} ${top + r}
                 V ${PAD.top + plotH} Z`
              : null;
          const hint = w.future
            ? `${shortDate(w.start)} – ${shortDate(w.end)} · upcoming`
            : `${shortDate(w.start)} – ${shortDate(w.end)} · ${w.logged} logged (` +
              `${w.plus_minus > 0 ? "+" : ""}${w.plus_minus} vs target) · ${w.accepted} accepted`;
          return (
            <g key={w.start} className="bar-slot">
              {path ? <path d={path} className="bar" /> : null}
              <rect x={cx - slot / 2} y={PAD.top} width={slot} height={plotH + PAD.bottom} className="hit"
                data-hint={hint} aria-label={hint} />
            </g>
          );
        })}
        {/* Target reference line, above the bars so it never drowns. */}
        <line x1={PAD.left} x2={W - PAD.right} y1={y(target)} y2={y(target)} className="la-target-line" />
        <text x={W - PAD.right + 4} y={y(target) + 3} className="tick">Target {target}</text>
        {done.length > 1 ? <polyline points={acceptedPoints} className="la-accepted-line" /> : null}
        {lastDone ? <circle cx={lastDoneX} cy={y(lastDone.accepted)} r="4" className="la-accepted-dot" /> : null}
      </svg>
    </div>
  );
}

export default function LoggedAcceptedHeadline({ report }) {
  // Numbers always show; the graph defaults ON (first impressions — the demo
  // audience won't find a ▾ they don't know about) but the toggle is
  // REMEMBERED per machine (orders:breakdown pattern), so folding it away
  // sticks for daily use. Decided 2026-07-18 (Robert delegated; his wife
  // voted on-by-default).
  const [showChart, setShowChart] = useState(() => {
    try { return localStorage.getItem("jobsLogged:graph") !== "0"; } catch { return true; }
  });
  const toggleChart = () =>
    setShowChart((v) => {
      try { localStorage.setItem("jobsLogged:graph", v ? "0" : "1"); } catch { /* ignore */ }
      return !v;
    });
  const weeks = report.months.flatMap((m) => m.weeks.map((w) => ({ ...w, month: m.month })));
  const done = weeks.filter((w) => !w.future);
  if (done.length === 0) return null;

  const logged = done.reduce((s, w) => s + w.logged, 0);
  const accepted = done.reduce((s, w) => s + w.accepted, 0);
  const onTarget = done.filter((w) => w.plus_minus >= 0).length;
  const rate = logged > 0 ? Math.round((accepted / logged) * 100) : 0;

  // vs last year over COMPLETED months only — a like-for-like window.
  const priorYear = report.prior_years[0];
  const completedMonths = report.months.filter(
    (m) => m.weeks.length > 0 && m.weeks.every((w) => !w.future)
  );
  let deltaLogged = null;
  let deltaAccepted = null;
  let deltaLabel = "";
  if (completedMonths.length > 0 && priorYear) {
    const cur = completedMonths.reduce(
      (s, m) => ({ l: s.l + m.logged, a: s.a + m.accepted }),
      { l: 0, a: 0 }
    );
    const prior = completedMonths.reduce(
      (s, m) => ({ l: s.l + m.prior[0].logged, a: s.a + m.prior[0].accepted }),
      { l: 0, a: 0 }
    );
    deltaLogged = cur.l - prior.l;
    deltaAccepted = cur.a - prior.a;
    const first = completedMonths[0].month.slice(0, 3);
    const last = completedMonths[completedMonths.length - 1].month.slice(0, 3);
    deltaLabel = `vs ${priorYear} · ${first === last ? first : `${first}–${last}`}`;
  }

  return (
    <div className="border px-3 pt-2 pb-2 mb-3">
      <div className="d-flex align-items-start justify-content-between flex-wrap column-gap-4 row-gap-2 pt-1">
        <div className="d-flex align-items-start column-gap-4 flex-wrap">
          <Stat label={`Logged · ${report.year}`} value={logged.toLocaleString()} delta={deltaLogged} deltaLabel={deltaLabel} />
          <Stat label="Accepted" value={accepted.toLocaleString()} delta={deltaAccepted} deltaLabel={deltaLabel} />
          <div className="vr" />
          <Stat label="Acceptance rate" value={`${rate}%`} />
          <Stat label={`Weeks on target (${report.weekly_target}/wk)`} value={`${onTarget} / ${done.length}`} />
        </div>
        <div className="d-flex align-items-center column-gap-3 text-muted" style={{ fontSize: "0.72rem" }}>
          {showChart ? (
            <>
              <span><span className="la-legend-swatch-bar me-1" />Logged</span>
              <span><span className="la-legend-swatch-line me-1" />Accepted</span>
            </>
          ) : null}
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-secondary text-decoration-none fs-6 lh-1 d-print-none"
            data-hint={showChart ? "Hide graph" : "Show graph"}
            aria-expanded={showChart}
            aria-label={showChart ? "Hide graph" : "Show graph"}
            onClick={toggleChart}
          >
            {showChart ? "▴" : "▾"}
          </button>
        </div>
      </div>
      {showChart ? <WeeklyChart weeks={weeks} target={report.weekly_target} /> : null}
    </div>
  );
}
